import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { savePdfA4Portrait_Results } from "@/lib/pdf";
import { ChevronLeft, Share2, Printer, Check } from "lucide-react";

const PLACEMENT_LABEL = {
  FIRST: "1st", SECOND: "2nd", THIRD: "3rd",
  HIGHLY_COMMENDED: "Highly Commended", COMMENDED: "Commended", NONE: "—",
};

// Look up the current portal token from either the URL (?token=…) OR the
// path-style /portal/share/<token> route. The token must travel with every
// public API call so the backend can scope responses to one club only.
function useShareToken() {
  const [params] = useSearchParams();
  return params.get("token") || params.get("share_token") || "";
}

// Append token to whatever URL query the calling page is using so we never
// accidentally leak a request that has no tenant scope.
function withToken(extra, token) {
  const out = { ...(extra || {}) };
  if (token) out.share_token = token;
  return out;
}

/**
 * Swap the page's <link rel="manifest"> for a dynamic in-memory manifest
 * whose `start_url` points back to /portal?token=<this token> while the
 * user is browsing the public portal.
 *
 * Why: the default /manifest.json declares `start_url: "/"`. If a guest
 * taps "Add to Home Screen" while viewing /portal?token=XYZ, the icon
 * would otherwise open `/`, dumping them on the marketing landing /
 * /login screen instead of their results. We override the manifest in
 * place so the install captures the right URL (Android Chrome reads
 * manifest.start_url; iOS Safari 16.4+ also honours it). On unmount we
 * restore the original manifest so the admin console keeps its own.
 */
// localStorage key the portal saves its token under so the standalone
// boot-redirect in App.js can recover it if the manifest's start_url is
// ignored by the browser (iOS Safari sometimes refuses to honour blob:
// manifests, in which case start_url falls back to '/' from the static
// admin manifest — landing PWA-installed users on the login page).
const PORTAL_LAST_TOKEN_KEY = "ccs_last_portal_token";

function useDynamicPortalManifest(token, clubName) {
  useEffect(() => {
    if (!token) return;

    // Belt-and-braces #1 — remember the token so a standalone-mode boot
    // arriving at "/" can redirect itself back to the portal.
    try {
      localStorage.setItem(PORTAL_LAST_TOKEN_KEY, token);
    } catch (_) {
      /* ignore */
    }

    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;

    const originalHref = link.getAttribute("href");
    const title = clubName ? `${clubName} — Results` : "Camera Club Results";
    // Belt-and-braces #2 — emit ABSOLUTE URLs in the swap. Some browsers
    // (notably Safari) refuse to resolve relative URLs against blob:
    // manifest documents and silently fall back to start_url="/" from
    // the static manifest. Absolute URLs sidestep that quirk entirely.
    const origin = window.location.origin;
    const startUrl = `${origin}/portal?token=${encodeURIComponent(token)}`;
    const manifest = {
      name: title,
      short_name: clubName ? clubName.slice(0, 12) : "Results",
      description: "Public competition results portal.",
      start_url: startUrl,
      scope: `${origin}/portal`,
      display: "standalone",
      orientation: "any",
      background_color: "#0a0a0a",
      theme_color: "#11061d",
      icons: [
        { src: `${origin}/icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
        { src: `${origin}/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "any" },
        { src: `${origin}/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
      ],
    };
    const blob = new Blob([JSON.stringify(manifest)], {
      type: "application/manifest+json",
    });
    const blobUrl = URL.createObjectURL(blob);
    link.setAttribute("href", blobUrl);

    // iOS Safari < 16.4 uses meta tags instead of the manifest for the
    // home-screen icon name. Update both so the installed icon reads
    // "<Club> — Results".
    const appleTitleMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    );
    const origAppleTitle = appleTitleMeta?.getAttribute("content");
    if (appleTitleMeta) appleTitleMeta.setAttribute("content", title);

    return () => {
      link.setAttribute("href", originalHref || "/manifest.json");
      URL.revokeObjectURL(blobUrl);
      if (appleTitleMeta && origAppleTitle != null) {
        appleTitleMeta.setAttribute("content", origAppleTitle);
      }
    };
  }, [token, clubName]);
}

export function PortalLayout({ children, breadcrumbs = [], clubName, clubLogo, token }) {
  const [copied, setCopied] = useState(false);
  useDynamicPortalManifest(token, clubName);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // clipboard write can fail in some embedded browsers — silently ignore
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header
        className="px-8 py-5 flex items-center justify-between"
        style={{ borderBottom: "1px solid #1f1f1f" }}
      >
        <Link
          to={token ? `/portal?token=${token}` : "/portal"}
          className="block flex items-center gap-3"
        >
          {clubLogo && (
            <img
              src={clubLogo}
              alt="Club logo"
              className="h-12 w-12 object-contain"
              data-testid="portal-club-logo"
            />
          )}
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              {clubName ? "Public results" : "Camera Club Score"}
            </div>
            <div className="text-xl font-semibold mt-1">
              {clubName || "Public Results Portal"}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          {token && (
            <button
              onClick={copyLink}
              className="ccs-btn-ghost flex items-center gap-2 text-sm"
              title="Copy this private share link"
              data-testid="share-link-button"
            >
              {copied ? <Check size={14} /> : <Share2 size={14} />}
              {copied ? "Copied!" : "Share"}
            </button>
          )}
          <Link to="/login" className="text-amber-400 text-sm">Sign in →</Link>
        </div>
      </header>
      {breadcrumbs.length > 0 && (
        <div className="px-8 py-3 flex items-center gap-2 text-xs muted-text"
          style={{ borderBottom: "1px solid #131313" }}>
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span>·</span>}
              {b.to ? (
                <Link to={b.to} className="hover:text-white">{b.label}</Link>
              ) : (
                <span className="text-white">{b.label}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      {children}
      <footer className="px-8 py-6 text-center muted-text text-xs"
        style={{ borderTop: "1px solid #131313", marginTop: 60 }}>
        Powered by Camera Club Score
      </footer>
    </div>
  );
}

export function PortalHome() {
  const token = useShareToken();
  const [club, setClub] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [comps, setComps] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setErr("This public results link is missing or invalid. Please use the link your club sent you.");
      return;
    }
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/public/club", { params: { share_token: token } }),
          api.get("/public/seasons", { params: { share_token: token } }),
        ]);
        setClub(c.data);
        setSeasons(s.data);
        const active = s.data.find((x) => x.is_active) || s.data[0];
        if (active) setSeasonId(active.id);
      } catch (ex) {
        setErr(
          ex.response?.status === 404
            ? "This share link is no longer active. Ask the club admin for a fresh link."
            : "Couldn't load results — please try again in a moment."
        );
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!seasonId || !token) return;
    (async () => {
      const c = await api.get("/public/competitions", {
        params: withToken({ season_id: seasonId }, token),
      });
      setComps(c.data);
    })();
  }, [seasonId, token]);

  if (err) {
    return (
      <PortalLayout>
        <main className="p-12 max-w-2xl mx-auto">
          <div className="ccs-card text-center">
            <div className="text-amber-400 mono text-xs uppercase tracking-widest mb-3">No link</div>
            <h1 className="text-2xl font-semibold mb-3">Public results portal</h1>
            <p className="muted-text">{err}</p>
          </div>
        </main>
      </PortalLayout>
    );
  }

  const seasonName = seasons.find((s) => s.id === seasonId)?.season_name;

  return (
    <PortalLayout clubName={club?.name} clubLogo={club?.logo_data_url} token={token}>
      <main className="p-8 max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="ccs-label mb-2">Season</div>
            <h1 className="text-4xl font-semibold tracking-tight">{seasonName || club?.name || ""}</h1>
          </div>
          {seasons.length > 1 && (
            <select
              className="ccs-input" style={{ width: 240 }}
              value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
              data-testid="portal-season-filter"
            >
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>{s.season_name}{s.is_active ? " (active)" : ""}</option>
              ))}
            </select>
          )}
        </div>

        <div className="ccs-card">
          <h2 className="text-lg font-medium mb-4">Competitions</h2>
          {comps.length === 0 ? (
            <div className="muted-text text-sm">No competitions yet.</div>
          ) : (
            <ul className="divide-y divide-neutral-900" data-testid="portal-competitions">
              {comps.map((c) => (
                <li key={c.id} className="py-3">
                  <Link
                    to={`/portal/competition/${c.id}?token=${token}`}
                    className="flex justify-between items-center hover:text-amber-400"
                    data-testid={`portal-comp-${c.id}`}
                  >
                    <div>
                      <div className="font-medium">{c.competition_name}</div>
                      <div className="text-xs mono muted-text">
                        {formatDMY(c.competition_date)} · {c.competition_type} · {c.category}
                      </div>
                    </div>
                    <span className="text-amber-400 text-sm">View →</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </PortalLayout>
  );
}

export function PortalCompetition() {
  const { competitionId } = useParams();
  const token = useShareToken();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setErr("This share link is missing — please use the link your club sent you.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/public/competition/${competitionId}`, {
          params: { share_token: token },
        });
        setData(data);
      } catch (ex) {
        setErr(ex.response?.status === 404
          ? "This competition is no longer available or the share link has been rotated."
          : "Couldn't load this competition — please try again.");
      }
    })();
  }, [competitionId, token]);

  if (err) {
    return (
      <PortalLayout token={token}>
        <main className="p-12 max-w-2xl mx-auto">
          <div className="ccs-card text-center">
            <div className="muted-text">{err}</div>
          </div>
        </main>
      </PortalLayout>
    );
  }
  if (!data) return <div className="p-12 muted-text">Loading…</div>;
  const { competition, season, entries, club_name: clubName } = data;

  // Print HTML mirrors savePdfA4Portrait_Results in /lib/pdf.js so the
  // "Print" and "Download PDF" buttons produce a visually identical
  // sheet. Column widths, header shading, monospace/bold treatments,
  // placement short codes, and the tinted placement column all match
  // the autoTable config there. If pdf.js changes, keep this template
  // in step.
  const printResults = () => {
    const w = window.open("", "_blank");
    if (!w) return alert("Pop-up blocked — please allow pop-ups for this site.");

    const PLACEMENT_SHORT = { FIRST: "1", SECOND: "2", THIRD: "3", HIGHLY_COMMENDED: "HC", COMMENDED: "C", NONE: "" };
    const PLACEMENT_BG = {
      FIRST: "#FEF3C7", SECOND: "#F3F4F6", THIRD: "#FFEDD5",
      HIGHLY_COMMENDED: "#ECFEFF", COMMENDED: "#F5F3FF",
    };
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));

    const rows = entries.map((e, i) => {
      const placeShort = PLACEMENT_SHORT[e.placement] || "";
      const placeBg = PLACEMENT_BG[e.placement];
      const placeStyle = placeBg ? ` style="background:${placeBg};font-weight:700"` : "";
      return `
      <tr>
        <td class="idx">${i + 1}</td>
        <td class="pid">${esc(e.photographer_code)}</td>
        <td>${esc(e.photographer_name)}</td>
        <td>${esc(e.image_title)}</td>
        <td class="score">${e.score == null ? "" : esc(e.score)}</td>
        <td class="place"${placeStyle}>${esc(placeShort)}</td>
      </tr>`;
    }).join("");

    const meta = [
      competition.competition_date ? `Date: ${formatDMY(competition.competition_date)}` : null,
      competition.competition_type ? `Type: ${competition.competition_type}` : null,
      competition.category ? `Category: ${competition.category}` : null,
      competition.round_number ? `Round: ${competition.round_number}` : null,
      competition.judge_name ? `Judge: ${competition.judge_name}` : null,
      season ? `Season: ${season.season_name}` : null,
    ].filter(Boolean).map(esc).join("   ·   ");

    w.document.write(`<!doctype html><html><head>
      <meta charset="utf-8">
      <title>${esc(competition.competition_name)} — Results</title>
      <style>
        @page { size: A4 portrait; margin: 10mm; }
        html, body { background: #fff; color: #111;
          font-family: Helvetica, Arial, sans-serif; margin: 0; padding: 0; }
        .sheet { padding: 12mm 10mm; max-width: 190mm; margin: 0 auto; }
        h1 { text-align: center; margin: 0; font-size: 16pt; font-weight: 700; }
        h2 { text-align: center; margin: 2mm 0 1mm; font-size: 13pt; font-weight: 700; }
        .meta { text-align: center; font-size: 9pt; color: #464646; }
        .subtitle { text-align: center; font-size: 8pt; letter-spacing: 0.06em;
          color: #6e6e6e; margin-top: 1mm; }
        hr.rule { border: 0; border-top: 0.4mm solid #111; margin: 2mm 0 3mm; }
        table { border-collapse: collapse; width: 100%; font-size: 9pt;
          color: #111; }
        thead th { background: #F3F3F3; color: #222; font-weight: 700;
          font-size: 7.5pt; padding: 1.5mm; text-align: left;
          border: 0.2mm solid #999; }
        tbody td { padding: 1.2mm 1.5mm; border: 0.2mm solid #999;
          vertical-align: top; }
        .idx { width: 9mm; text-align: center; color: #666; }
        .pid { width: 20mm; font-weight: 700; }
        .score { width: 18mm; text-align: center; font-weight: 700; }
        .place { width: 18mm; text-align: center; }
        .legend { font-size: 7.5pt; color: #555; margin-top: 3mm; }
        @media print { .sheet { padding: 0; } }
      </style></head><body>
      <div class="sheet">
        ${clubName ? `<h1>${esc(clubName)}</h1>` : ""}
        <h2>${esc(competition.competition_name)}</h2>
        <div class="meta">${meta}</div>
        <div class="subtitle">RESULTS REPORT</div>
        <hr class="rule" />
        <table>
          <thead><tr>
            <th style="width:9mm">#</th>
            <th style="width:20mm">I.D.</th>
            <th>Photographer</th>
            <th>Image Title</th>
            <th style="width:18mm;text-align:center">Score</th>
            <th style="width:18mm;text-align:center">Place</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="legend">Place codes: 1=1st &nbsp; 2=2nd &nbsp; 3=3rd &nbsp; HC=Highly Commended &nbsp; C=Commended</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 200);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <PortalLayout
      token={token}
      clubName={clubName}
      breadcrumbs={[
        { label: clubName || "Portal", to: `/portal?token=${token}` },
        { label: season?.season_name || "Season" },
        { label: competition.competition_name },
      ]}
    >
      <main className="p-8 max-w-5xl mx-auto">
        <Link
          to={`/portal?token=${token}`}
          className="inline-flex items-center gap-1 muted-text text-sm hover:text-white mb-3"
        >
          <ChevronLeft size={14} /> Back to results
        </Link>
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="ccs-label mb-2">Results</div>
            <h1 className="text-3xl font-semibold tracking-tight">{competition.competition_name}</h1>
            <div className="mono muted-text mt-1 text-sm">
              {formatDMY(competition.competition_date)} · {competition.competition_type} · {competition.category}
              {competition.judge_name ? ` · Judge: ${competition.judge_name}` : ""}
            </div>
          </div>
          {entries.length > 0 && (
            <div className="flex gap-2 self-start">
              <button
                type="button"
                onClick={printResults}
                className="ccs-btn-ghost flex items-center gap-2"
                data-testid="portal-print-results"
                title="Open a printable view in a new tab"
              >
                <Printer size={16} /> Print
              </button>
              <button
                type="button"
                onClick={() => savePdfA4Portrait_Results({ competition, season, entries, clubName })}
                className="ccs-btn-primary flex items-center gap-2"
                data-testid="portal-download-results-pdf"
                title="Download a printable A4 PDF of these results"
              >
                <Printer size={16} /> Download PDF
              </button>
            </div>
          )}
        </div>

        <div className="ccs-card p-0 overflow-x-auto">
          <table className="ccs-table" data-testid="portal-results-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Rank</th>
                <th>Photographer</th>
                <th>Image Title</th>
                <th>Score</th>
                <th>Place</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={`${e.photographer_code}-${i}`}>
                  <td className="mono text-amber-400 font-medium">#{i + 1}</td>
                  <td>
                    <div className="font-medium">{e.photographer_name}</div>
                    <div className="text-xs mono muted-text">{e.photographer_code}</div>
                  </td>
                  <td>{e.image_title}</td>
                  <td className="mono text-right" style={{ paddingRight: 24, fontWeight: 600 }}>{e.score}</td>
                  <td>
                    <span className={`placement-badge placement-${e.placement}`}>
                      {PLACEMENT_LABEL[e.placement] || "—"}
                    </span>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 muted-text">No entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </PortalLayout>
  );
}
